import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { ChevronDown, ChevronRightIcon, PencilIcon } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';

import { InfoPage } from '@/components/info-page';
import { createTableRowSelectionCell, EntityTable } from '@/components/table/entity-table';
import { TableErrorState } from '@/components/table/table-error-state';
import { TableFilterDropdown } from '@/components/table/table-filter-dropdown';
import { TableFilterInputField } from '@/components/table/table-filter-field';
import { TableFilterResetButton } from '@/components/table/table-filter-reset-button';
import { Tooltip } from '@/components/tooltip';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { StudentStatusBadge } from '@/components/students/student-status-badge';
import { StudentBulkUpdate } from '@/components/students/student-bulk-update';

import { EmptyArray, getStudentsWithLearningIssues } from '@/lib/api-calls';
import { useNavigateSearchState, validateRouteRole } from '@/lib/routing';
import { DEFAULT_PAGE_SIZE, paginationRequestSchema } from '@/lib/pagination';
import { usePaginatedFilters } from '@/lib/filtering';
import { AirId, FrequentTimeZones, formatInstant, isAirAdmin, toRawAirId } from '@airreading/common';
import { normalizeNewlines } from '@/lib/string-utils';

import { useAllSchools, useSchoolAndOrgOptions } from '@/lib/api-call-hooks';
import { airServerErrorFrom, axiosErrorFrom, useErrorToast } from '@/lib/error-handling';
import { useSorting } from '@/lib/sorting';
import { airColumnHelper } from '@/lib/tables';
import { useRowsBulkSelection } from '@/lib/bulk-selection';
import { getStudentYearOptions } from '@/lib/student-utils';
import { getStudentFeedbackStatusOptions } from '@/lib/student-feedback-utils';
import { TableFilterPlainDateRangePicker } from '@/components/table/table-filter-plain-date-range-picker';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { AirIdDisplay } from '@/components/air-id-display';

import {
  getStudentsWithLearningIssuesSortableColumnSchema,
  getStudentsWithLearningIssuesSortOrderSchema,
  LearningIssueMetricFilter,
  StudentFeedbackStatus,
  StudentFeedbackStatusLabelMap,
  StudentStatus,
  StudentWithLearningIssue,
  StudentYearColorMap,
  studentStatusSchema,
  studentYearOption,
  studentYearSchema,
  studentFeedbackStatusSchema,
  GetStudentsWithLearningIssuesSortableColumn,
  toSessionFeedbackCompoundId,
} from '@airreading/common/data';

import { CreateStudent } from '@/routes/_layout/users/-components/create-student';
import { CreateStudentsByCsv } from '@/routes/_layout/users/-components/create-students-by-csv';
import { studentsTabs } from '../-components/tabs';
import { useTimeZone } from '@/auth/useTimeZone';
import { RightDrawer } from '@/components/ui/right-drawer';
import { InfoPageSuspenseBoundary } from '@/components/info-page-suspense-boundary';
import { StudentReviewHistory } from '../-components/student-review-history';
import { EditStudentFeedbackModal } from '../-components/edit-student-feedback-modal';

const studentsSearchSchema = paginationRequestSchema.extend({
  query: z.string().optional().catch(''),
  schools: z.array(z.string()).optional(),
  grades: z.array(studentYearSchema).optional(),
  statuses: z.array(studentStatusSchema).optional(),
  issueStartDateFrom: z.string().optional(),
  issueStartDateTo: z.string().optional(),
  issueStatuses: z.array(studentFeedbackStatusSchema).optional(),
  metricFilter: z.enum(['mastery_check', 'fluency']).optional(),
  sortBy: getStudentsWithLearningIssuesSortOrderSchema.optional(),
});

const METRIC_FILTER_OPTIONS: {
  value: LearningIssueMetricFilter;
  id: string;
  label: string;
}[] = [
  { value: LearningIssueMetricFilter.mastery_check, id: 'mastery-check', label: 'Mastery Check' },
  { value: LearningIssueMetricFilter.fluency, id: 'fluency-issue', label: 'Fluency Issue' },
];

function isMetricFilterValue(value: string | undefined): value is LearningIssueMetricFilter {
  return METRIC_FILTER_OPTIONS.some((o) => o.value === value);
}

export const Route = createFileRoute('/_layout/students/learning-issue/')({
  beforeLoad: validateRouteRole(isAirAdmin),
  component: StudentsWithLearningIssuesIndex,
  validateSearch: studentsSearchSchema,
});

const col = airColumnHelper<StudentWithLearningIssue>(getStudentsWithLearningIssuesSortableColumnSchema);

// Helper to format note with 2-line truncation and hover tooltip
const NoteCell = ({ note }: { note: string }) => {
  const normalizedNote = normalizeNewlines(note);
  const lines = normalizedNote.split('\n');
  const displayLines = lines.slice(0, 2);
  const hasMore = lines.length > 2 || normalizedNote.length > 100;

  if (!hasMore) {
    return <div className="line-clamp-2">{displayLines.join('\n')}</div>;
  }

  return (
    <Tooltip trigger={<div className="line-clamp-2">{displayLines.join('\n')}</div>}>
      <div className="max-w-md whitespace-pre-wrap">{normalizedNote}</div>
    </Tooltip>
  );
};

// Helper to render clickable dates for session feedbacks
const FeedbackDatesCell = ({
  dates,
  studentId,
}: {
  dates: Array<{ date: Temporal.Instant | string; sessionId: string; coachId: string }>;
  studentId: string;
}) => {
  const timeZone = FrequentTimeZones.UsCentral;
  if (!dates || dates.length === 0) return <span>-</span>;

  return (
    <div className="flex flex-wrap gap-1">
      {dates.map(({ date, sessionId, coachId }, idx) => {
        // Convert string to Temporal.Instant if needed
        const instant = typeof date === 'string' ? Temporal.Instant.from(date) : date;
        const formatted = formatInstant(instant, timeZone).formattedDate;
        return (
          <span key={idx}>
            <Link
              to="/sessions/session-feedbacks/$feedbackId"
              params={{ feedbackId: toSessionFeedbackCompoundId({ sessionId, studentId, coachId }) }}
              className="text-blue-600 hover:text-blue-800"
              search={{ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE }}
            >
              {formatted}
            </Link>
            {idx < dates.length - 1 && <span>, </span>}
          </span>
        );
      })}
    </div>
  );
};

const buildColumns = (
  schoolIdToDistrictName: Record<string, string>,
  timeZone: string,
  onSelectStudent: (student: StudentWithLearningIssue) => void,
  onEditSuccess: () => void,
) => [
  createTableRowSelectionCell(col),
  col.accessor((row) => row, {
    id: 'edit',
    header: '',
    size: 50,
    cell: ({ getValue }) => {
      const student = getValue();
      if (!student.learningIssueStudentFeedback) {
        return null;
      }
      return (
        <EditStudentFeedbackModal
          studentFeedback={student.learningIssueStudentFeedback}
          trigger={<PencilIcon size={16} className="cursor-pointer" />}
          onSuccess={onEditSuccess}
        />
      );
    },
  }),
  col.accessor((row) => row, {
    id: 'fullNameOrEmail',
    header: 'Student Name',
    size: 150,
    cell: ({ getValue }) => {
      const student = getValue();
      return (
        <span className="text-blue-600 hover:text-blue-800 cursor-pointer" onClick={() => onSelectStudent(student)}>
          {student.fullNameOrEmail}
        </span>
      );
    },
  }),
  col.accessor('email', {
    id: 'email',
    header: 'Email',
    size: 180,
  }),
  col.accessor('grade', {
    id: 'grade',
    header: 'Grade',
    size: 80,
    cell: ({ getValue }) => {
      const grade = getValue();
      if (!grade) return studentYearOption(grade);
      const backgroundColor = StudentYearColorMap[grade];
      return (
        <div className="min-w-[80px] flex justify-start">
          <span className="text-xs rounded-full px-2 py-1 min-w-9 text-center" style={{ backgroundColor }}>
            {studentYearOption(grade)}
          </span>
        </div>
      );
    },
  }),
  col.accessor((row) => row, {
    id: 'school',
    header: 'School - District',
    size: 200,
    enableSorting: false,
    cell: ({ getValue }) => {
      const school = getValue()?.school;
      const districtName = school?.id ? schoolIdToDistrictName[school.id] : null;
      return (
        school && (
          <Link to={'/orgs/schools/$schoolId/schedules'} params={{ schoolId: school.id }} className="text-blue-600">
            {school.name} {districtName ? `- ${districtName}` : ''}
          </Link>
        )
      );
    },
  }),
  col.accessor('status', {
    id: 'status',
    header: 'Status',
    size: 120,
    cell: ({ getValue }) => {
      return (
        <div className="flex gap-x-1">
          <StudentStatusBadge status={getValue()} />
        </div>
      );
    },
  }),
  col.accessor((row) => row.learningIssueData?.issueStartDate, {
    id: 'issueStartDate',
    header: 'Issue start date',
    size: 120,
    cell: ({ getValue }) => {
      const issueStartDate = getValue();
      if (!issueStartDate) return <span>-</span>;
      // Convert string to Temporal.Instant if needed
      const instant = typeof issueStartDate === 'string' ? Temporal.Instant.from(issueStartDate) : issueStartDate;
      return <span>{formatInstant(instant, timeZone).formattedDate}</span>;
    },
  }),
  col.accessor((row) => row.learningIssueData?.reviewer, {
    id: 'reviewer',
    header: 'Reviewer',
    size: 150,
    cell: ({ getValue }) => {
      const reviewer = getValue();
      if (!reviewer) return <span>-</span>;
      const name = [reviewer.firstName, reviewer.lastName].filter(Boolean).join(' ').trim();
      return <span>{name || reviewer.email}</span>;
    },
  }),
  col.accessor((row) => row.learningIssueData?.issueStatus, {
    id: 'issueStatus',
    header: 'Issue status',
    size: 120,
    cell: ({ getValue }) => {
      const status = getValue();
      if (!status) return <span>{StudentFeedbackStatusLabelMap[StudentFeedbackStatus.need_review]}</span>;
      return <span>{StudentFeedbackStatusLabelMap[status]}</span>;
    },
  }),
  col.accessor((row) => row.learningIssueData?.lessonTooEasyCount ?? 0, {
    id: 'lessonTooEasyCount',
    header: 'Lesson too easy',
    size: 120,
    cell: ({ getValue }) => {
      return <span>{getValue()}</span>;
    },
  }),
  col.accessor((row) => row.learningIssueData?.lessonTooEasyDates ?? [], {
    id: 'lessonTooEasyDates',
    header: 'Lesson too easy date',
    size: 200,
    cell: ({ row, getValue }) => {
      const dates = getValue() ?? [];
      return <FeedbackDatesCell dates={dates} studentId={row.original.id} />;
    },
  }),
  col.accessor((row) => row.learningIssueData?.lessonTooHardCount ?? 0, {
    id: 'lessonTooHardCount',
    header: 'Lesson too hard',
    size: 120,
    cell: ({ getValue }) => {
      return <span>{getValue()}</span>;
    },
  }),
  col.accessor((row) => row.learningIssueData?.lessonTooHardDates ?? [], {
    id: 'lessonTooHardDates',
    header: 'Lesson too hard date',
    size: 200,
    cell: ({ row, getValue }) => {
      const dates = getValue() ?? [];
      return <FeedbackDatesCell dates={dates} studentId={row.original.id} />;
    },
  }),
  col.accessor((row) => row.learningIssueData?.levelTooEasyCount ?? 0, {
    id: 'levelTooEasyCount',
    header: 'Level too easy',
    size: 120,
    cell: ({ getValue }) => {
      return <span>{getValue()}</span>;
    },
  }),
  col.accessor((row) => row.learningIssueData?.levelTooEasyDates ?? [], {
    id: 'levelTooEasyDates',
    header: 'Level too easy date',
    size: 200,
    cell: ({ row, getValue }) => {
      const dates = getValue() ?? [];
      return <FeedbackDatesCell dates={dates} studentId={row.original.id} />;
    },
  }),
  col.accessor((row) => row.learningIssueData?.levelTooHardCount ?? 0, {
    id: 'levelTooHardCount',
    header: 'Level too hard',
    size: 120,
    cell: ({ getValue }) => {
      return <span>{getValue()}</span>;
    },
  }),
  col.accessor((row) => row.learningIssueData?.levelTooHardDates ?? [], {
    id: 'levelTooHardDates',
    header: 'Level too hard date',
    size: 200,
    cell: ({ row, getValue }) => {
      const dates = getValue() ?? [];
      return <FeedbackDatesCell dates={dates} studentId={row.original.id} />;
    },
  }),
  col.accessor((row) => row.learningIssueData?.note ?? '', {
    id: 'issueNotes',
    header: 'Issue notes',
    size: 250,
    cell: ({ getValue }) => {
      const note = getValue();
      return <NoteCell note={note} />;
    },
  }),
  col.accessor((row) => row.learningIssueData?.lastSession, {
    id: 'lastSession',
    header: 'Last session',
    size: 120,
    cell: ({ row, getValue }) => {
      const lastSession = getValue();
      if (!lastSession) return <span>-</span>;

      // Convert string to Temporal.Instant if needed
      const instant = typeof lastSession.date === 'string' ? Temporal.Instant.from(lastSession.date) : lastSession.date;
      const formatted = formatInstant(instant, timeZone).formattedDate;
      return (
        <Link
          to="/sessions/session-feedbacks/$feedbackId"
          params={{
            feedbackId: toSessionFeedbackCompoundId({
              sessionId: lastSession.sessionId,
              studentId: row.original.id,
              coachId: lastSession.coachId,
            }),
          }}
          className="text-blue-600 hover:text-blue-800"
          search={{ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE }}
        >
          {formatted}
        </Link>
      );
    },
  }),
  col.accessor((row) => row.learningIssueData?.activeClassIds, {
    id: 'activeClassIds',
    header: 'Class ID',
    size: 150,
    cell: ({ getValue }) => {
      const activeClassIds = getValue();
      if (!activeClassIds || activeClassIds.length === 0) return <span>-</span>;
      return (
        <div className="flex flex-wrap gap-2">
          {activeClassIds.map((classId) => (
            <AirIdDisplay key={classId} id={AirId('cls', toRawAirId(classId))} />
          ))}
        </div>
      );
    },
  }),
];

function StudentsWithLearningIssuesIndex() {
  const { timeZone } = useTimeZone();
  const { data: allSchools } = useAllSchools();
  const [selectedStudent, setSelectedStudent] = useState<StudentWithLearningIssue | null>(null);

  const schoolIdToDistrictName = Object.fromEntries(allSchools.map((school) => [school.id, school.org.name]));

  const handleSelectStudent = useCallback((student: StudentWithLearningIssue) => {
    setSelectedStudent(student);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setSelectedStudent(null);
  }, []);

  const [sortBy, didChangeSortBy] = useNavigateSearchState(Route.id, 'sortBy');
  const { sorting, ...tableSortingOptions } = useSorting<GetStudentsWithLearningIssuesSortableColumn>([
    sortBy,
    didChangeSortBy,
  ]);

  const {
    pageIndex,
    pageSize,
    onPaginationChange,
    filters: {
      query: [[userNameQuery, activeUserNameQuery], setUserNameQuery],
      schools: [[selectedSchools, activeSelectedSchools], setSelectedSchools],
      grades: [[selectedGrades, activeSelectedGrades], setSelectedGrades],
      statuses: [[selectedStatuses, activeSelectedStatuses], setSelectedStatuses],
      issueStartDateFrom: [[selectedIssueStartDateFrom], setIssueStartDateFrom],
      issueStartDateTo: [[selectedIssueStartDateTo], setIssueStartDateTo],
      issueStatuses: [[selectedIssueStatuses, activeIssueStatuses], setIssueStatuses],
      metricFilter: [[selectedMetricFilter], setMetricFilter],
    },
    resetFilters,
  } = usePaginatedFilters(Route.id, [
    'query',
    'schools',
    'grades',
    'statuses',
    'issueStartDateFrom',
    'issueStartDateTo',
    'issueStatuses',
    'metricFilter',
  ]);

  const applyMetricFilter = useCallback(
    (next: LearningIssueMetricFilter | undefined) => setMetricFilter(next),
    [setMetricFilter],
  );

  const allSchoolOptions = useSchoolAndOrgOptions({
    orgIds: [],
  });
  const {
    data: response,
    isPending,
    error,
    refetch,
  } = useQuery(
    getStudentsWithLearningIssues({
      pageIndex,
      pageSize,
      filters: {
        query: activeUserNameQuery,
        schools: activeSelectedSchools,
        grades: activeSelectedGrades,
        statuses: activeSelectedStatuses,
        issueStartDateFrom: selectedIssueStartDateFrom,
        issueStartDateTo: selectedIssueStartDateTo,
        issueStatuses: activeIssueStatuses,
        metricFilter: selectedMetricFilter,
      },
      sortBy,
    }),
  );

  const students = useMemo(() => response?.students || EmptyArray, [response]);
  useErrorToast(error, 'Error loading students. Please try again.');

  const { rowSelection, rowDataSelection, onRowSelectionChange, getRowId, resetAllRowSelection } =
    useRowsBulkSelection<StudentWithLearningIssue>({
      getRowId: (row) => row.id,
      rowIdToData: (id) => students.find((student) => student.id === id) as StudentWithLearningIssue | undefined,
    });

  const onSuccess = () => {
    resetAllRowSelection();
    refetch();
  };

  const handleEditSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  const columns = useMemo(
    () => buildColumns(schoolIdToDistrictName, timeZone, handleSelectStudent, handleEditSuccess),
    [schoolIdToDistrictName, timeZone, handleSelectStudent, handleEditSuccess],
  );

  const table = useReactTable({
    data: students,
    columns,
    state: {
      pagination: {
        pageIndex,
        pageSize,
      },
      sorting,
      rowSelection,
    },
    rowCount: response?.total || 0,
    onRowSelectionChange,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange,
    manualPagination: true,
    enableColumnResizing: true,
    ...tableSortingOptions,
  });

  const filterLeftComponents = useMemo(
    () => (
      <>
        <TableFilterInputField
          placeholder="Search users by name or email"
          value={userNameQuery ?? ''}
          onChange={(event) => {
            setUserNameQuery(event.target.value);
          }}
        />
        <RadioGroup
          className="flex items-center gap-4 w-min"
          value={selectedMetricFilter ?? ''}
          onValueChange={(value) => {
            if (isMetricFilterValue(value)) applyMetricFilter(value);
          }}
        >
          {METRIC_FILTER_OPTIONS.map(({ value, id, label }) => (
            <div
              key={value}
              className="flex items-center gap-2 cursor-pointer rounded-[6px] bg-white p-3"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                applyMetricFilter(selectedMetricFilter === value ? undefined : value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  applyMetricFilter(selectedMetricFilter === value ? undefined : value);
                }
              }}
              role="button"
              tabIndex={0}
              aria-pressed={selectedMetricFilter === value}
              aria-label={label}
            >
              <RadioGroupItem value={value} id={id} />
              <Label htmlFor={id} className="block">
                {label}
              </Label>
            </div>
          ))}
        </RadioGroup>
        <TableFilterDropdown
          title="School"
          options={allSchoolOptions}
          selectedValues={selectedSchools ?? []}
          onChange={setSelectedSchools}
        />
        <TableFilterPlainDateRangePicker
          fromPlainDateString={selectedIssueStartDateFrom}
          toPlainDateString={selectedIssueStartDateTo}
          onRangeSelect={(range) => {
            setIssueStartDateFrom(range.fromPlainDateString);
            setIssueStartDateTo(range.toPlainDateString);
          }}
          placeholder="Issue start date"
        />
        <TableFilterDropdown
          title="Issue status"
          options={getStudentFeedbackStatusOptions().filter(
            (option) => option.value !== StudentFeedbackStatus.resolved,
          )}
          selectedValues={selectedIssueStatuses ?? []}
          onChange={setIssueStatuses}
        />
        <TableFilterDropdown
          title="Grade"
          options={getStudentYearOptions()}
          selectedValues={selectedGrades ?? []}
          onChange={setSelectedGrades}
        />
        <TableFilterDropdown
          title="Status"
          options={[
            { value: StudentStatus.enrolled, label: 'Enrolled' },
            { value: StudentStatus.dropped, label: 'Dropped' },
            { value: StudentStatus.program_concluded, label: 'Program concluded' },
          ]}
          selectedValues={selectedStatuses ?? []}
          onChange={setSelectedStatuses}
        />
        <TableFilterResetButton show={true} onClick={resetFilters} />
      </>
    ),
    [
      selectedMetricFilter,
      applyMetricFilter,
      userNameQuery,
      setUserNameQuery,
      allSchoolOptions,
      selectedSchools,
      setSelectedSchools,
      selectedIssueStartDateFrom,
      selectedIssueStartDateTo,
      setIssueStartDateFrom,
      setIssueStartDateTo,
      selectedIssueStatuses,
      setIssueStatuses,
      selectedGrades,
      setSelectedGrades,
      selectedStatuses,
      setSelectedStatuses,
      resetFilters,
    ],
  );

  const bulkActionsMenuItems = (
    <>
      <StudentBulkUpdate
        confirmMessage="Are you sure you want to drop"
        invalidMessage="Only enrolled students can be selected."
        options={{ status: StudentStatus.dropped }}
        onSuccess={onSuccess}
        students={rowDataSelection}
        title="Mark drop off"
        validation={(student) => student.status === StudentStatus.enrolled}
      />
      <StudentBulkUpdate
        confirmMessage="Are you sure you want to conclude the program for"
        invalidMessage="Only students with no program concluded status can be selected."
        options={{ status: StudentStatus.program_concluded }}
        onSuccess={onSuccess}
        students={rowDataSelection}
        title="Mark program concluded"
        validation={(student) => student.status !== StudentStatus.program_concluded}
      />
    </>
  );

  const topBarRightComponents = (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button className="ml-auto hidden h-8 lg:flex">
          Add students
          <ChevronDown className="mr-2 h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[150px]">
        <CreateStudent
          onSuccess={refetch}
          trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Add a student</DropdownMenuItem>}
        />
        <CreateStudentsByCsv
          onSuccess={refetch}
          trigger={<DropdownMenuItem onSelect={(e) => e.preventDefault()}>Import from CSV</DropdownMenuItem>}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <InfoPage headerText="Students" tabs={studentsTabs('Learning Issue')}>
      <EntityTable
        table={table}
        showPagination={true}
        isLoading={isPending}
        hasError={!!error}
        emptyState={'No students found'}
        errorState={<TableErrorState refetchAction={refetch} />}
        filters={{ filterLeftComponents }}
        bulkActionsMenuItems={bulkActionsMenuItems}
        topBarRightComponents={topBarRightComponents}
      />
      {selectedStudent && (
        <RightDrawer
          open
          onOpenChange={(open) => {
            if (!open) {
              handleCloseDrawer();
            }
          }}
          title={
            <div>
              <div className="text-[30px] font-semibold mb-2 max-[900px]:text-left flex items-center gap-4">
                Review history
                <Link
                  to={'/students/$studentId'}
                  params={{ studentId: selectedStudent.id }}
                  search={{ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE }}
                  className="text-[21px] leading-[38px] text-blue-600 hover:text-blue-800 flex items-center gap-2"
                >
                  {selectedStudent.fullNameOrEmail} <ChevronRightIcon size={18} strokeWidth={3} />
                </Link>
              </div>
            </div>
          }
          shouldConfirmOnCloseModal={false}
          dismissible={false}
          widthClass="bg-[#F7F7F9] w-[40%] min-w-[900px] max-w-[90%] max-[900px]:w-[100%] max-[900px]:min-w-[480px] max-[900px]:max-w-full max-[900px]:sm:max-w-full"
        >
          {() => {
            return (
              <InfoPageSuspenseBoundary
                onError={(e) => {
                  const axiosError = axiosErrorFrom(e);
                  const airServerError = airServerErrorFrom(e);

                  return {
                    allowRetry: axiosError?.response?.status !== 404,
                    errorHeader:
                      airServerError?.userFacingMessage ||
                      'Failed to load student details. Please try again or contact Air Reading support',
                  };
                }}
              >
                <StudentReviewHistory studentId={selectedStudent.id} />
              </InfoPageSuspenseBoundary>
            );
          }}
        </RightDrawer>
      )}
    </InfoPage>
  );
}
